import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import PasswordInput from "../components/PasswordInput";
import {
  Crown, Zap, Sparkles, Building2, Check, MessageCircle, LogIn, ArrowRight,
  ShieldCheck, Smartphone, BarChart3, Truck, FileText,
} from "lucide-react";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const ICONES = { start: Sparkles, pro: Zap, premium: Crown, corporativo: Building2 };
const CORES = {
  start: {
    badge: "bg-slate-100 text-slate-700",
    card: "border-slate-200 hover:border-slate-400",
    btn: "bg-slate-900 hover:bg-slate-800 text-white",
    accent: "text-slate-800",
  },
  pro: {
    badge: "bg-blue-100 text-blue-700",
    card: "border-blue-300 hover:border-blue-500 ring-2 ring-blue-100",
    btn: "bg-blue-600 hover:bg-blue-700 text-white",
    accent: "text-blue-700",
  },
  premium: {
    badge: "bg-amber-100 text-amber-700",
    card: "border-amber-300 hover:border-amber-500 ring-2 ring-amber-100",
    btn: "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white",
    accent: "text-amber-700",
  },
  corporativo: {
    badge: "bg-purple-100 text-purple-700",
    card: "border-purple-200 hover:border-purple-400",
    btn: "bg-purple-600 hover:bg-purple-700 text-white",
    accent: "text-purple-700",
  },
};

const PLANO_LABELS = { start: "Start", pro: "Pro", premium: "Premium", corporativo: "Corporativo" };

function formatPreco(v) {
  if (v == null) return "Sob consulta";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function LandingPage({ setIsAuthenticated }) {
  const [catalogo, setCatalogo] = useState([]);
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [planoEscolhido, setPlanoEscolhido] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", senha: "" });
  const [signupForm, setSignupForm] = useState({ nome: "", email: "", senha: "", nome_loja: "", telefone: "" });
  const [assinarJa, setAssinarJa] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/planos/catalogo`);
        setCatalogo(data);
      } catch (e) {
        // silencioso — UI mostra estado vazio
      }
    })();
  }, []);

  const abrirCadastro = (plano) => {
    if (plano.id === "corporativo") {
      const url = `https://wa.me/5567991992593?text=${encodeURIComponent(
        "Olá! Gostaria de conhecer o plano Corporativo do Compromisso Digital."
      )}`;
      window.open(url, "_blank");
      return;
    }
    setPlanoEscolhido(plano);
    setSignupOpen(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const emailNorm = (loginForm.email || "").trim().toLowerCase();
      const { data } = await axios.post(`${API}/auth/login`, { email: emailNorm, senha: loginForm.senha });
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      toast.success("Login realizado com sucesso!");
      setIsAuthenticated(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Email ou senha inválidos");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!planoEscolhido) return;
    setLoading(true);
    try {
      const emailNorm = (signupForm.email || "").trim().toLowerCase();
      const payload = {
        ...signupForm,
        email: emailNorm,
        plano: planoEscolhido.id,
      };
      const { data } = await axios.post(`${API}/auth/register`, payload);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      // Se marcou "Assinar agora sem trial" → vai direto para checkout Mercado Pago
      // (corporativo abre WhatsApp de contato comercial, não é fluxo de checkout).
      if (assinarJa && planoEscolhido.id !== "corporativo") {
        try {
          const { data: chk } = await axios.post(
            `${API}/planos/checkout`,
            { plano: planoEscolhido.id },
            { headers: { Authorization: `Bearer ${data.token}` } }
          );
          if (chk.status === "checkout_criado" && chk.init_point) {
            toast.success(`Conta criada! Redirecionando para o pagamento...`);
            window.location.href = chk.init_point;
            return;
          }
          toast.info(chk.message || "Conta criada. Você pode assinar depois em Planos.");
        } catch {
          toast.info("Conta criada. Abra a área Planos para finalizar o pagamento.");
        }
      } else {
        toast.success(`Conta criada no plano ${planoEscolhido.nome}!`);
      }
      setIsAuthenticated(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="border-b border-gray-200/60 backdrop-blur-sm bg-white/70 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-compromisso.png" alt="Compromisso Digital" className="w-10 h-10 rounded-xl shrink-0" />
            <div className="leading-tight">
              <p className="font-bold" style={{ fontFamily: "Work Sans" }}>Compromisso Digital</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Gestão de vendas por rota</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="#planos"
              className="hidden sm:inline text-sm font-medium text-gray-700 hover:text-blue-700"
              data-testid="link-planos-header"
            >
              Planos
            </a>
            <a
              href="#recursos"
              className="hidden sm:inline text-sm font-medium text-gray-700 hover:text-blue-700"
              data-testid="link-recursos-header"
            >
              Recursos
            </a>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => setLoginOpen(true)}
              data-testid="btn-entrar-header"
            >
              <LogIn className="h-4 w-4" />
              Entrar na minha conta
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-12 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-100 text-blue-800 rounded-full text-xs font-bold tracking-wider uppercase mb-6">
          <Sparkles className="h-3.5 w-3.5" /> Plataforma completa para distribuidores
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight" style={{ fontFamily: "Work Sans" }}>
          Sua rota de vendas no <br className="hidden sm:block" />
          <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
            controle absoluto
          </span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
          Promissórias, caixa do carro, controle de rotas, gestão financeira PF/PJ — e a sua{" "}
          <strong className="text-emerald-700">Loja Digital com App próprio</strong> incluída em{" "}
          <strong>todos os planos</strong>.
        </p>

        {/* Badge "Loja Digital incluída" — destaque do diferencial */}
        <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 text-sm font-bold text-emerald-800 shadow-sm">
          <Smartphone className="h-4 w-4" />
          App próprio no celular dos seus clientes — em todos os planos
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a
            href="#planos"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white font-bold shadow-lg shadow-blue-200"
            data-testid="hero-cta-ver-planos"
          >
            Ver planos
            <ArrowRight className="h-4 w-4" />
          </a>
          <Button
            variant="outline"
            className="h-12 px-6 gap-2"
            onClick={() => setLoginOpen(true)}
            data-testid="hero-cta-entrar"
          >
            <LogIn className="h-4 w-4" /> Já tenho conta
          </Button>
        </div>

        {/* social proof / mini stats */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto">
          <Stat valor="20+" label="empresas ativas" />
          <Stat valor="10mil+" label="promissórias geradas" />
          <Stat valor="R$ 197" label="a partir de /mês" />
          <Stat valor="100%" label="brasileiro" />
        </div>
      </section>

      {/* Recursos */}
      <section id="recursos" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold" style={{ fontFamily: "Work Sans" }}>
            Tudo que o vendedor de rota precisa
          </h2>
          <p className="text-gray-600 mt-2">
            Da emissão da promissória ao acerto do caixa — sem planilha, sem fricção.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Feature icon={FileText} title="Promissórias digitais" desc="Crie, edite e compartilhe em PDF com 1 toque. Foto da venda + recobrança automática." />
          <Feature icon={Truck} title="Caixa do Carro" desc="Acerto por rota, múltiplas formas de pagamento e PDF para o motorista." />
          <Feature icon={BarChart3} title="Gestão Financeira" desc="Contas a pagar PF/PJ, fluxo de caixa, metas diárias e semanais." />
          <Feature icon={Smartphone} title="Loja Digital + App próprio" desc="Seu catálogo no celular do cliente, com app dedicado, pedidos via WhatsApp e checkout InfinitePay (Pro/Premium)." />
        </div>
      </section>

      {/* Planos */}
      <section id="planos" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-100 text-amber-800 rounded-full text-xs font-bold tracking-wider uppercase mb-3">
            <Crown className="h-3.5 w-3.5" /> Planos
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold" style={{ fontFamily: "Work Sans" }}>
            Escolha seu plano e <span className="text-blue-600">comece hoje</span>
          </h2>
          <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
            Cresça sem trocar de sistema. Faça upgrade quando precisar de mais carros ou recursos.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {catalogo.map((p) => {
            const Icon = ICONES[p.id] || Sparkles;
            const cores = CORES[p.id] || CORES.start;
            const destaque = p.id === "pro";
            return (
              <Card
                key={p.id}
                className={`relative overflow-hidden transition-all ${cores.card} ${
                  destaque ? "scale-[1.02] shadow-2xl" : "shadow-sm"
                }`}
                data-testid={`landing-plano-card-${p.id}`}
              >
                {destaque && (
                  <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl">
                    MAIS POPULAR
                  </div>
                )}
                {p.id !== "start" && p.id !== "corporativo" && (
                  <div className="absolute top-0 left-0 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1 rounded-br-xl">
                    14 DIAS GRÁTIS
                  </div>
                )}
                <CardHeader className="pt-8 pb-2">
                  <div className={`w-12 h-12 rounded-xl ${cores.badge} flex items-center justify-center mb-2`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <CardTitle className={`text-2xl ${cores.accent}`}>{p.nome}</CardTitle>
                  <p className="text-xs text-gray-500">{p.descricao}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-3xl font-extrabold">{formatPreco(p.preco)}</p>
                    {p.preco != null && <p className="text-xs text-gray-500">por mês</p>}
                  </div>
                  <div className="space-y-1.5 min-h-[180px]">
                    {p.destaques.map((d, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                        <span className="text-gray-700">{d}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    className={`w-full gap-2 h-11 ${cores.btn}`}
                    onClick={() => abrirCadastro(p)}
                    data-testid={`landing-btn-assinar-${p.id}`}
                  >
                    {p.id === "corporativo" ? (
                      <>
                        <MessageCircle className="h-4 w-4" /> Falar com especialista
                      </>
                    ) : (
                      <>
                        Começar com {p.nome} <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          Pagamento seguro via Mercado Pago. Cartão de crédito ou débito, com renovação automática mensal.
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-8 py-8 text-center text-xs text-gray-500">
        © {new Date().getFullYear()} Compromisso Digital — Feito para distribuidores brasileiros.
      </footer>

      {/* Modal de login */}
      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent className="max-w-sm" data-testid="login-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5 text-blue-600" /> Entrar na minha conta
            </DialogTitle>
            <DialogDescription>Acesse sua área administrativa.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                required
                placeholder="seunome@email.com"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                data-testid="login-email-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Use um email completo com <strong>@</strong> e <strong>.com</strong> (ex.: <em>nome@email.com</em>).
              </p>
            </div>
            <div>
              <Label>Senha</Label>
              <PasswordInput
                required
                value={loginForm.senha}
                onChange={(e) => setLoginForm({ ...loginForm, senha: e.target.value })}
                data-testid="login-password-input"
              />
            </div>
            <DialogFooter className="pt-2">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
                data-testid="login-submit-button"
              >
                {loading ? "Entrando..." : "Entrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de cadastro com plano pré-selecionado */}
      <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
        <DialogContent className="max-w-md" data-testid="signup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-500" /> Criar conta —{" "}
              <span className="text-blue-700">{planoEscolhido?.nome || ""}</span>
            </DialogTitle>
            <DialogDescription>
              {planoEscolhido
                ? `Plano ${planoEscolhido.nome} • ${formatPreco(planoEscolhido.preco)}/mês`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSignup} className="space-y-3">
            <div>
              <Label>Seu nome *</Label>
              <Input
                required
                value={signupForm.nome}
                onChange={(e) => setSignupForm({ ...signupForm, nome: e.target.value })}
                data-testid="signup-nome-input"
              />
            </div>
            <div>
              <Label>Nome da empresa/loja</Label>
              <Input
                value={signupForm.nome_loja}
                onChange={(e) => setSignupForm({ ...signupForm, nome_loja: e.target.value })}
                data-testid="signup-loja-input"
              />
            </div>
            <div>
              <Label>WhatsApp (opcional)</Label>
              <Input
                value={signupForm.telefone}
                onChange={(e) => setSignupForm({ ...signupForm, telefone: e.target.value })}
                placeholder="(67) 99999-0000"
                data-testid="signup-telefone-input"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                required
                placeholder="seunome@email.com"
                value={signupForm.email}
                onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                data-testid="signup-email-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Use um email completo com <strong>@</strong> e <strong>.com</strong> (ex.: <em>nome@email.com</em>). Esse email será seu login.
              </p>
            </div>
            <div>
              <Label>Senha *</Label>
              <PasswordInput
                required
                minLength={4}
                value={signupForm.senha}
                onChange={(e) => setSignupForm({ ...signupForm, senha: e.target.value })}
                data-testid="signup-senha-input"
              />
            </div>
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800 space-y-2">
              <p>⚡ <strong>14 dias grátis</strong> no plano <strong>{planoEscolhido?.nome}</strong> — testa sem compromisso.</p>
              {planoEscolhido?.id !== "corporativo" && (
                <label className="flex items-start gap-2 cursor-pointer bg-white/70 rounded p-2 border border-blue-200">
                  <input
                    type="checkbox"
                    checked={assinarJa}
                    onChange={(e) => setAssinarJa(e.target.checked)}
                    className="mt-0.5"
                    data-testid="signup-assinar-ja"
                  />
                  <span>
                    <strong>Prefiro assinar agora</strong> e ir direto para o pagamento no Mercado Pago
                    ({formatPreco(planoEscolhido?.preco)}/mês) — sem período de teste.
                  </span>
                </label>
              )}
            </div>
            <DialogFooter className="pt-2 flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setSignupOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 gap-2"
                data-testid="signup-submit-button"
              >
                {loading ? "Criando..." : assinarJa ? (
                  <>Criar conta e assinar agora <ArrowRight className="h-4 w-4" /></>
                ) : (
                  <>Criar conta {planoEscolhido?.nome} <ArrowRight className="h-4 w-4" /></>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ valor, label }) {
  return (
    <div className="rounded-xl bg-white border border-gray-200 px-3 py-3 shadow-sm">
      <p className="text-xl font-extrabold text-gray-900">{valor}</p>
      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</p>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-xl bg-white border border-gray-200 p-4 hover:border-blue-300 hover:shadow-md transition-all">
      <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center mb-3">
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-bold text-sm">{title}</p>
      <p className="text-xs text-gray-600 mt-1 leading-relaxed">{desc}</p>
    </div>
  );
}
